import { QuerySwitch } from 'components/QuerySwitch';
import { DashboardEmptyView } from 'pages/DashboardPage/DashboardEmptyView';
import { DashboardPageContext } from 'pages/DashboardPage/DashboardPage.context';
import { FC, Fragment, useEffect } from 'react';
import { GQLAPIContextType } from 'types/common.types';
import { DashboardUpcomingShiftsPageQuery } from 'types/graphql';
import { withMemo } from 'utils/withMemo';
import { useDashboardPageUnmountContext } from 'pages/DashboardPage/DashboardPageUnmount.context';
import { queryRefetchService } from 'services/QueryRefetchService';
import { BKJPagination } from '@awais4123/fillify-design-library';
import {
  BKJStickyTableHead,
  BKJTableBody,
  BKJTableFooter,
  BKJTableWrapper,
} from 'components/BKJTable/BKJTable.styled';
import { BKJTableSubHeader } from 'components/BKJTable/BKJTableSubHeader';
import { GroupOrdersComponent } from 'pages/DashboardPage/GroupOrdersComponent';
import { SHIFTS_WITH_ACTION_B2B_TABLE_COLUMNS_WIDTH } from 'pages/ShiftsListPage/ShiftsTable/ShiftsTable.constants';
import { useTranslation } from 'hooks/useTranslation';
import { ChangeEventType } from '@awais4123/fillify-design-library/lib/common.types';
import { IDirectionType } from 'components/BKJTable/BKJTableSubHeader/BKJTableSubHeader.types';
import { ShiftsB2BTableColumnNameType } from 'pages/ShiftsListPage/ShiftsTable/ShiftsB2BTable.types';
import { onSort } from 'pages/ShiftsListPage/ShiftsTable/ShiftsTable.utils';
import { shiftsListInitialSortFields } from 'pages/ShiftsPage/ShiftsPage.constants';

const DashboardUpcomingShiftsPageList: FC<GQLAPIContextType<DashboardUpcomingShiftsPageQuery>> = ({
  data,
  prevPage,
  nextPage,
  firstPage,
  lastPage,
  currentPage,
  values,
  onChange,
}) => {
  const { unmountDashboard } = useDashboardPageUnmountContext();
  const { t } = useTranslation();

  useEffect(() => {
    queryRefetchService.addDataQuery('DASHBOARD_PAGE', 'upcoming', unmountDashboard);
    return () => {
      queryRefetchService.removeDataQuery('DASHBOARD_PAGE', 'upcoming');
    };
  }, [unmountDashboard]);
  const sortFields = values.sortField
    ? [JSON.parse(values.sortField)]
    : shiftsListInitialSortFields;

  function TableSort(e: ChangeEventType) {
    onChange(onSort(e));
  }

  const getColumnDirection = (column: ShiftsB2BTableColumnNameType): IDirectionType => {
    const field = sortFields.find((field) => field.name === column);
    if (field) {
      return field.direction;
    }
    return 'default';
  };

  if (!data) return null;
  if (!data?.fastGroupOrders) return null;
  if (!data?.fastGroupOrders?.nodes) return null;
  if (data?.fastGroupOrders?.nodes.length === 0 && !data?.fastGroupOrders?.pageInfo.hasNextPage)
    return <DashboardEmptyView />;

  return (
    <Fragment>
      <BKJTableWrapper>
        <BKJTableBody gridTemplateColumns={SHIFTS_WITH_ACTION_B2B_TABLE_COLUMNS_WIDTH}>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              title={t('shifts_calendar:SHIFT_DATE')}
              name="startAt"
              onChevronClick={TableSort}
              direction={getColumnDirection('startAt')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="janeName"
              title={t('common:TEAM_MEMBER')}
              onChevronClick={TableSort}
              direction={getColumnDirection('janeName')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="positionName"
              title={t('common:POSITION')}
              onChevronClick={TableSort}
              direction={getColumnDirection('positionName')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="status"
              title={t('shifts_calendar:STATUS')}
              direction="default"
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="startAt"
              title={t('shifts_calendar:START_TIME')}
              direction={getColumnDirection('startAt')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="endAt"
              title={t('shifts_calendar:END_TIME')}
              direction={getColumnDirection('endAt')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="unpaidBreakTime"
              title={t('shifts_calendar:UNPAID_BREAKTIME')}
              direction={getColumnDirection('unpaidBreakTime')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="totalDuration"
              title={t('common:TOTAT_DURATION')}
              onChevronClick={TableSort}
              direction={getColumnDirection('totalDuration')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead>
            <BKJTableSubHeader
              name="id"
              title={t('shifts_calendar:SHIFT_NO')}
              onChevronClick={TableSort}
              direction={getColumnDirection('id')}
            />
          </BKJStickyTableHead>
          <BKJStickyTableHead css="position: sticky; right: 0; box-shadow: 0px -4px 8px rgb(13 51 32 / 30%); overflow-y: hidden;">
            <BKJTableSubHeader name="action" title={t('team_member:ACTION')} direction="default" />
          </BKJStickyTableHead>
          {data?.fastGroupOrders?.nodes.map((record, groupIndex) => {
            const { orders } = record;
            return (
              orders.length > 0 &&
              orders
                .filter(Boolean)
                .map((order, orderIndex) => (
                  <GroupOrdersComponent
                    key={order.id}
                    groupIndex={groupIndex}
                    orderIndex={orderIndex}
                  />
                ))
            );
          })}
        </BKJTableBody>
      </BKJTableWrapper>
      <BKJTableFooter>
        <BKJPagination
          currentPage={currentPage}
          pageCount={data.fastGroupOrders.pageCount}
          onFirstPage={firstPage}
          onPrevPage={prevPage}
          onNextPage={nextPage}
          onLastPage={lastPage}
        />
      </BKJTableFooter>
    </Fragment>
  );
};
export const DashboardUpcomingShiftsPage: FC = withMemo(() => {
  return <QuerySwitch context={DashboardPageContext} component={DashboardUpcomingShiftsPageList} />;
});
